/**
 * ICORS API Service
 * Refactored from legacy connectionManager
 *
 * Purpose: Fetch customer offers from ICORS (Integrated Customer Offer Recommendation System)
 *
 * Dependencies:
 * - @repo/shared-utils/cache - Function memoization
 * - @repo/shared-utils/session - User session management
 * - connection-manager (GlobalApisService) - External npm package
 */

import { createCacheAsync } from "@repo/shared-utils/cache";
import { getSessionData } from "@repo/shared-utils/session";
import { request } from "../core/request";
import { API_RESPONSE_CODES, DEFAULT_CONNECTION_FROM, OPERATION_IDS, SOURCE_ID } from "./constants";
import { IOffersResponse, IOffers, IOffersPromptResponse } from "../types/icorsTypes";

/**
 * Get customer offers from ICORS
 *
 * Features:
 * - Automatically fetches mobile number from session
 * - Caches results per mobile number to avoid duplicate API calls
 * - Returns structured offer list with status codes
 *
 * @returns Promise<IOffersResponse> - Customer offers with status
 *
 * @example
 * ```typescript
 * const offers = await getIcors();
 * if (offers.statusCode === 200) {
 *   console.log("Offers:", offers.offerList);
 * }
 * ```
 */
export const getIcors = async (): Promise<IOffersResponse> => {
  try {
    const { mobileNo } = await getSessionData();
    const response = (await createCacheAsync(
      `${OPERATION_IDS.offers}-${mobileNo}`,
      async () =>
        await request<IOffersResponse, { mobileNo: string; sourceId: string }>({
          operationId: OPERATION_IDS.offers,
          requestData: {
            mobileNo,
            sourceId: SOURCE_ID,
          },
          connectionFrom: DEFAULT_CONNECTION_FROM,
        })
    )) as IOffersResponse;
    return response;
  } catch (error) {
    console.error(error);
    return {
      statusCode: API_RESPONSE_CODES.STATUS_CODE_7002,
      description: "Error fetching offers data",
      data: { offers: [] },
    };
  }
};

/**
 * Get ICORS offer type from offer data
 * @param offerData - Offer data
 * @returns ICORS offer type or empty string
 */
export const getIcorsOfferType = (offerData?: IOffers): string =>
  offerData?.icorsOfferType ? offerData.icorsOfferType : "";

/**
 * Get offer ID from offer data
 * @param offerData - Offer data
 * @returns Offer ID or empty string
 */
export const getOfferId = (offerData?: IOffers): string => (offerData?.id ? offerData.id : "");

/**
 * Get offer amount from offer data
 * @param offerData - Offer data
 * @returns Offer amount or 0
 */
export const getOfferAmount = (offerData?: IOffers): number =>
  offerData?.icorsAmount ? +offerData.icorsAmount : 0;

/**
 * Get ICORS offers by product
 * @param icorsProduct - ICORS product name(s) (comma-separated)
 * @returns Array of filtered offers
 */
export const offerAPI = async (icorsProduct: string): Promise<IOffers[]> => {
  const product = icorsProduct?.length ? icorsProduct : "";
  const offerIdentifier = product.toUpperCase().replace(/\s/g, "").split(",");
  const response = await getIcors();
  const offer =
    response?.data?.offers?.filter((offer: IOffers) =>
      offerIdentifier.includes(offer.icorsProduct)
    ) || [];
  return offer;
};

// For offerList response format
export const getIcorsData = async (): Promise<IOffersPromptResponse> => {
  try {
    const { mobileNo } = await getSessionData();

    const response = (await createCacheAsync(
      `${OPERATION_IDS.offers}-prompt-${mobileNo}`,
      async () =>
        await request<IOffersPromptResponse, { mobile: string; sourceId: string }>({
          operationId: OPERATION_IDS.offers,
          requestData: {
            mobile: mobileNo,
            sourceId: "2",
          },
          connectionFrom: DEFAULT_CONNECTION_FROM,
        })
    )) as IOffersPromptResponse;
    return response;
  } catch (error) {
    console.error(error);
    return {
      statusCode: API_RESPONSE_CODES.STATUS_CODE_7002,
      description: "Error fetching offers data",
      offerList: [],
    };
  }
};
